import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/apiClient";
import PermissionGate from "@/components/PermissionGate";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wand2, Plus, Trash2, FileText, Undo2, CheckCircle2, Ban } from "lucide-react";

const MODES = ["Cash", "UPI", "Bank Transfer", "Credit Card", "Debit Card", "Cheque", "Other"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const PAY_STYLE = { Pending: "bg-amber-100 text-amber-700", Partial: "bg-blue-100 text-blue-700", Paid: "bg-accent/10 text-accent", Overdue: "bg-red-100 text-red-600", Refunded: "bg-slate-100 text-slate-600" };
const INST_STYLE = { Pending: "bg-amber-100 text-amber-700", Paid: "bg-accent/10 text-accent", Overdue: "bg-red-100 text-red-600", Waived: "bg-slate-100 text-slate-600" };
const today = new Date().toISOString().slice(0, 10);

export default function FinanceSheet({ enrollment, open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [plan, setPlan] = useState({ count: 3, start_date: today, frequency: "monthly", replace: false });
  const [pay, setPay] = useState({ amount_received: 0, payment_mode: "UPI", payment_date: today, installment_id: "", type: "Installment", remarks: "" });
  const [isRefund, setIsRefund] = useState(false);

  const eid = enrollment?.id;
  const load = useCallback(async () => {
    if (!eid) return;
    const { data } = await api.get(`/enrollments/${eid}/finance`);
    setData(data);
  }, [eid]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const summary = data?.summary || {};
  const installments = data?.installments || [];
  const payments = data?.payments || [];

  const refresh = async () => { await load(); onChanged?.(); };

  const generate = async () => {
    try {
      await api.post(`/enrollments/${eid}/installments/generate`, { ...plan, count: Number(plan.count) });
      toast.success("Installment plan generated");
      setPlanOpen(false); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openPay = (installment_id, amount, refund) => {
    setIsRefund(!!refund);
    setPay({ amount_received: amount || 0, payment_mode: "UPI", payment_date: today, installment_id: installment_id || "", type: refund ? "Refund" : "Installment", remarks: "" });
    setPayOpen(true);
  };

  const submitPay = async () => {
    try {
      const url = isRefund ? `/enrollments/${eid}/refunds` : `/enrollments/${eid}/payments`;
      await api.post(url, { ...pay, amount_received: Number(pay.amount_received) });
      toast.success(isRefund ? "Refund recorded" : "Payment recorded");
      setPayOpen(false); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const waive = async (iid) => { try { await api.patch(`/enrollments/${eid}/installments/${iid}/waive`); toast.success("Waived"); refresh(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const delInst = async (iid) => { try { await api.delete(`/enrollments/${eid}/installments/${iid}`); toast.success("Removed"); refresh(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const delPay = async (pid) => { if (!window.confirm("Delete this payment?")) return; try { await api.delete(`/enrollments/${eid}/payments/${pid}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const receiptUrl = (pid) => `${API}/payments/${pid}/receipt`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="finance-sheet">
        <SheetHeader>
          <SheetTitle>Fees — {enrollment?.enrollment_code}</SheetTitle>
          <SheetDescription>{enrollment?.student_name} · {enrollment?.course_name}</SheetDescription>
        </SheetHeader>

        <div className="mt-5 grid grid-cols-4 gap-3">
          {[["Final Fee", summary.final_fee], ["Paid", summary.total_paid], ["Pending", summary.pending_amount]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-border p-3">
              <p className="text-xs text-muted-foreground">{l}</p>
              <p className="font-display text-lg font-bold text-secondary">{inr(v)}</p>
            </div>
          ))}
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs text-muted-foreground">Status</p>
            <Badge className={`mt-1 border-0 ${PAY_STYLE[summary.payment_status] || ""}`}>{summary.payment_status}</Badge>
          </div>
        </div>

        {/* Installments */}
        <div className="mt-6 flex items-center justify-between">
          <h4 className="font-display font-semibold text-secondary">Installment plan</h4>
          <PermissionGate permission="payment.create">
            <Button size="sm" variant="outline" onClick={() => setPlanOpen(true)} data-testid="generate-plan-button"><Wand2 className="mr-1.5 h-4 w-4" /> Generate plan</Button>
          </PermissionGate>
        </div>
        <div className="mt-2 rounded-xl border border-border">
          <Table>
            <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Due date</TableHead><TableHead>Amount</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {installments.length === 0 && <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground">No plan yet — generate one.</TableCell></TableRow>}
              {installments.map((i) => (
                <TableRow key={i.id} data-testid={`installment-row-${i.seq}`}>
                  <TableCell>{i.seq}</TableCell>
                  <TableCell>{i.due_date}</TableCell>
                  <TableCell>{inr(i.due_amount)}</TableCell>
                  <TableCell>{inr(i.paid_amount)}</TableCell>
                  <TableCell><Badge className={`border-0 ${INST_STYLE[i.status] || ""}`}>{i.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    {i.status !== "Paid" && i.status !== "Waived" && (
                      <PermissionGate permission="payment.create">
                        <Button size="sm" variant="ghost" title="Pay" onClick={() => openPay(i.id, Math.max(0, i.due_amount - i.paid_amount), false)} data-testid={`pay-installment-${i.seq}`}><CheckCircle2 className="h-4 w-4 text-accent" /></Button>
                      </PermissionGate>
                    )}
                    <PermissionGate permission="payment.refund"><Button size="sm" variant="ghost" title="Waive" onClick={() => waive(i.id)}><Ban className="h-4 w-4" /></Button></PermissionGate>
                    {(!i.paid_amount) && <PermissionGate permission="payment.update"><Button size="sm" variant="ghost" title="Delete" onClick={() => delInst(i.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></PermissionGate>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Payments */}
        <div className="mt-6 flex items-center justify-between">
          <h4 className="font-display font-semibold text-secondary">Payments &amp; refunds</h4>
          <div className="flex gap-2">
            <PermissionGate permission="payment.create"><Button size="sm" onClick={() => openPay("", 0, false)} data-testid="record-payment-button"><Plus className="mr-1.5 h-4 w-4" /> Payment</Button></PermissionGate>
            <PermissionGate permission="payment.refund"><Button size="sm" variant="outline" onClick={() => openPay("", 0, true)} data-testid="record-refund-button"><Undo2 className="mr-1.5 h-4 w-4" /> Refund</Button></PermissionGate>
          </div>
        </div>
        <div className="mt-2 rounded-xl border border-border">
          <Table>
            <TableHeader><TableRow><TableHead>Receipt</TableHead><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {payments.length === 0 && <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground">No payments recorded.</TableCell></TableRow>}
              {payments.map((p) => (
                <TableRow key={p.id} data-testid={`payment-row-${p.receipt_number}`}>
                  <TableCell className="font-medium">{p.receipt_number}</TableCell>
                  <TableCell>{p.payment_date}</TableCell>
                  <TableCell><Badge className={`border-0 ${p.amount_received < 0 ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-600"}`}>{p.type}</Badge></TableCell>
                  <TableCell className={p.amount_received < 0 ? "text-red-600" : ""}>{inr(p.amount_received)}</TableCell>
                  <TableCell>{p.payment_mode}</TableCell>
                  <TableCell className="text-right">
                    <a href={receiptUrl(p.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" title="Receipt"><FileText className="h-4 w-4" /></Button></a>
                    <PermissionGate permission="payment.delete"><Button size="sm" variant="ghost" onClick={() => delPay(p.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></PermissionGate>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </SheetContent>

      {/* Generate plan dialog */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent data-testid="plan-dialog">
          <DialogHeader><DialogTitle>Generate installment plan</DialogTitle><DialogDescription>Split the pending fee into scheduled installments.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Number of installments</Label><Input type="number" value={plan.count} onChange={(e) => setPlan((p) => ({ ...p, count: e.target.value }))} data-testid="plan-count-input" /></div>
            <div>
              <Label>Frequency</Label>
              <Select value={plan.frequency} onValueChange={(v) => setPlan((p) => ({ ...p, frequency: v }))}>
                <SelectTrigger data-testid="plan-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="fortnightly">Fortnightly</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>First due date</Label><Input type="date" value={plan.start_date} onChange={(e) => setPlan((p) => ({ ...p, start_date: e.target.value }))} data-testid="plan-start-input" /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={plan.replace} onChange={(e) => setPlan((p) => ({ ...p, replace: e.target.checked }))} /> Replace existing unpaid installments</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPlanOpen(false)}>Cancel</Button><Button onClick={generate} data-testid="confirm-plan-button">Generate</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record payment/refund dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>{isRefund ? "Record refund" : "Record payment"}</DialogTitle><DialogDescription>{isRefund ? "Refunds are stored as negative payments." : "Log a fee payment against this enrollment."}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Amount (₹)</Label><Input type="number" value={pay.amount_received} onChange={(e) => setPay((p) => ({ ...p, amount_received: e.target.value }))} data-testid="payment-amount-input" /></div>
            <div>
              <Label>Mode</Label>
              <Select value={pay.payment_mode} onValueChange={(v) => setPay((p) => ({ ...p, payment_mode: v }))}>
                <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Date</Label><Input type="date" value={pay.payment_date} onChange={(e) => setPay((p) => ({ ...p, payment_date: e.target.value }))} /></div>
            <div className="col-span-2"><Label>Remarks</Label><Input value={pay.remarks} onChange={(e) => setPay((p) => ({ ...p, remarks: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button><Button onClick={submitPay} data-testid="confirm-payment-button">{isRefund ? "Record refund" : "Record payment"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
